import { Entity } from "aframe";
import { AnchorEntity, AnchorComponent } from "../anchor";
import { AnimationActionInstance } from "../animation/animation-action";
import { AvatarSystem, AvatarActionFactory } from "../avatar";
import { InputActionDetail } from "../input/common";
import { InventoryItemComponent } from "../inventory-item";
import { SlotEntity, SlotProxyEntity, SlotEvent, SlotProxyComponent, SlotComponent } from "../slot";
import { Lazy } from "../utils";
import { InteractiveComponent } from "./button-and-trigger";
import { EventEmitterAction } from "./event-emitter-action";
import { AvatarGrabAction } from "./grab-action";
import { IInteractionTarget, InteractiveEntity } from "./interaction";



//Input is fresh 
const isFreshInput = (input : InputActionDetail) =>
{
	return Date.now() <= (input.timestamp + 50);
}

const isSubEntity = (entity : Entity, root : Entity) =>
{
	for(; entity && entity.components; entity = entity.parentElement as Entity)
	{
		if(entity === root)
			return true;
		if("ammo-body" in entity.components)
			break;
	}
}

const isActiveTarget = (entity : Entity, slot : Entity) =>
{
	for(let el = entity; el; el = el.parentEl!)
		if(!el.object3D.visible)
			return;

	const button = entity.components.button as InteractiveComponent;
	if(button && button.data && button.data.enabled)
		return true;

	const trigger = entity.components.trigger as InteractiveComponent;
	if(trigger && trigger.data && trigger.data.enabled)
		return true;

	const lever = entity.components.lever as InteractiveComponent;
	if(lever && lever.data && lever.data.enabled)
	 return true;

	const anchor = entity.components.anchor as AnchorComponent;
	if(anchor && anchor.data && anchor.data.enabled && anchor.fits(slot))
		return true;
}



function onceInArray(value : any, index : number, array : any[])
{
	while(--index >= 0)
		if(array[index] === value)
			return false;

	return true;
}



const sqrDistanceBetween = (a : Entity, b : Entity ) =>
{
	const aMatrix = a.object3D.matrixWorld.elements;
	const bMatrix = b.object3D.matrixWorld.elements;
	const dx = aMatrix[12] - bMatrix[12], dy = aMatrix[13] - bMatrix[13], dz = aMatrix[14] - bMatrix[14];
	return (dx * dx + dy * dy + dz * dz);
};

export class AvatarInteractionTarget implements IInteractionTarget
{
	initialized : boolean = false;
	finalized : boolean = false;

	locked : boolean = false;
	
	mainAction : string; //"do something" for single-button devices
	grabAction : string; //Take/Put/Drop
	fireAction : string; //Pull the Trigger
	useAction : string; //Activate
	inspectAction : string; //Inspect
	
	slot : SlotEntity | null = null;
	
	slotItemAnchors? : AnchorEntity[];
	
	slotItem? : AnchorEntity | null = null;
	currentTarget? : InteractiveEntity | SlotEntity | SlotProxyEntity;
	currentAnchor? : AnchorEntity; //Put this Anchor...
	currentSlot? : SlotEntity; //Into this Slot...

	previousSlotItem? : AnchorEntity | null = null;
	previousTarget? : InteractiveEntity | SlotEntity;
	previousAnchor? : AnchorEntity;
	previousSlot? : SlotEntity;

	updateNeeded? : boolean;
	
	distance : number = 0;
	ray? : Lazy<Entity>;

	updateListener : (event? : Event) => void;
	
	constructor(prefix:string) {
		this.mainAction = prefix + "Action";
		this.grabAction = prefix + "Grab";
		this.fireAction = prefix + "Fire";
		this.useAction = prefix + "Use";
		this.inspectAction = prefix + "Inspect";

		this.updateListener = (event? : Event) => { 
			this.updateNeeded = true; 
		};
	}

	init( slot : SlotEntity )
	{
		if(this.slot === slot)  //Already inited.
			return; 

		this.slot = slot;


		const scene = slot.sceneEl!;
		const avatarSystem = scene.systems.avatar as AvatarSystem;

		const itemAttachedListener = (event : SlotEvent) => {

			if(event.target !== slot)
				return;

			const attachedItem = event.detail.item!, attachedAnchor = event.detail.anchor!;

			this.slotItemAnchors = Array.from(attachedItem.querySelectorAll<AnchorEntity>("[anchor]"));
			if(attachedItem.components.anchor)
			this.slotItemAnchors.unshift(attachedItem as AnchorEntity);

			attachedAnchor.addEventListener("interactions-changed", this.updateListener);

			attachedAnchor.addEventListener("detached-from-slot", () =>
			{
				attachedAnchor.removeEventListener("interactions-changed", this.updateListener);
				delete this.slotItemAnchors;

				if(scene.is('vr-mode')) // Force immediate update : detector reactions might not be called anymore
					this.reset().finish(avatarSystem, true);
				else
					this.updateNeeded = true;

			}, {once:true} );


			if(scene.is('vr-mode')) // Force immediate update : detector reactions might not be called anymore
				this.reset().finish(avatarSystem, true);
			else
				this.updateNeeded = true;
		};



		slot.addEventListener("item-attached", itemAttachedListener as EventListener);
	}
	
	reset( ignoreSlotItem? : boolean, ray? : Lazy<Entity> ) : AvatarInteractionTarget {
		if(this.locked) return this;
		if(this.initialized) return this;

		this.initialized = true;
		this.finalized = false;

		this.previousTarget = this.currentTarget;
		delete this.currentTarget;
		this.previousAnchor = this.currentAnchor;
		delete this.currentAnchor;
		this.previousSlot = this.currentSlot;
		delete this.currentSlot;
		this.previousSlotItem = this.slotItem;
		this.slotItem = ignoreSlotItem ? null : (this.slot && this.slot.components.slot && this.slot.components.slot.data.anchor);
		this.ray = ray;
		this.distance = NaN;
		return this;
	}
	update(detectedEntity : Entity, collidingEntity? : Entity ) : AvatarInteractionTarget 
	{
		if(this.locked) return this;
		if(this.distance <= 0) return this; //Already found the best target

		for(let obj = detectedEntity.object3D; obj; obj = obj.parent!)
			if(obj === this.slot!.object3D)
				return this; //Do not detect objects linked to this.slot


		//let slot : SlotEntity | null = this.slot;
		let possibleTarget : InteractiveEntity | undefined = undefined;
		let possibleAnchor : AnchorEntity | undefined = undefined;
		let possibleSlot : SlotEntity | undefined = undefined;

		if(this.slotItem) //There's something is this slot : look for another slot to put the item into
		{
			//slot = detectedEntity as SlotEntity;
			possibleTarget = undefined; //Until we find a suitable anchor

			if( this.slotItemAnchors )
			{
				const possibleSlots = [ detectedEntity as SlotEntity ];

				// For now, Slots need their own, precise ammo-body. Don't look for child slots.
				// possibleSlots.push( ... detectedEntity.querySelectorAll("[slot]") as any );

				const slotProxy = detectedEntity.components["slot-proxy"] as SlotProxyComponent;
				if(slotProxy)
					possibleSlots.push( ...slotProxy.data );

				let dMin = NaN;
				for(let pSlot of possibleSlots )
				{
					const slot = pSlot && pSlot.components.slot;
					if(!(slot && slot.data))
						continue;

					//Allow detecting locked, empty slots as interaction targets, even if the grab action won't be "put".
					//To make one not detected, just disable it's ammo-body.
					if(slot.data.item) // || slot.data.locked)
						continue;

					for(let anchorEl of this.slotItemAnchors)
					{
						if(!anchorEl.components.anchor.data.enabled)
							continue;
	
						if(!anchorEl.components.anchor.fits(pSlot))
							continue;
	
						if( possibleTarget )
						{
							if( isNaN(dMin) )
								dMin = slot.sqrDistanceFrom(possibleTarget); 
							const d = slot.sqrDistanceFrom(anchorEl);
							if(d >= dMin)
								continue;
							dMin = d;
						}
						possibleSlot = pSlot;
						possibleTarget = detectedEntity;
						possibleAnchor = anchorEl;
					}
				}
			}

			if(!possibleTarget)
			{
				const invItem = (detectedEntity.components["inventory-item"] as InventoryItemComponent)?.data;
				const slotItemEl = this.slot!.components.slot.data.item!;
				if(slotItemEl && invItem?.accept?.length)
					for(let condition of invItem.accept)
						if(slotItemEl.matches(condition))
						{
							possibleSlot = this.slot!;
							possibleTarget = detectedEntity;
							break;
						}
			}
		}
		else //Nothing in slot : look for something to take, or a button to activate.
		{
			possibleSlot = this.slot!;
			if(detectedEntity.components.button || detectedEntity.components.trigger || detectedEntity.components.lever)
			{
				possibleTarget = detectedEntity;
			}
			else
			{
				const anchor = detectedEntity.components.anchor as AnchorComponent;
				if(anchor && anchor.fits(possibleSlot) && !possibleSlot.components.slot.data.anchor )
				{
					possibleTarget = detectedEntity;
				}
			}

			if(!possibleTarget)
			{
				const possibleTargets = Array.from(detectedEntity.querySelectorAll<Entity>("[anchor],[button],[trigger],[lever]"));
				const slot = this.slot!.components.slot;
				let dMin = NaN;
				for(let tgt of possibleTargets)
				{
					if(!isActiveTarget(tgt, possibleSlot))
						continue;

					if( possibleTarget )
					{
						if( isNaN(dMin) )
							dMin = slot.sqrDistanceFrom(possibleTarget); 
						const d = slot.sqrDistanceFrom(tgt);
						if(d >= dMin)
							continue;
						dMin = d;
					}
					possibleTarget = tgt;
				}
			}
		}

		if(!(possibleSlot && possibleTarget)) return this;

		if(this.currentTarget) //Already a target, choose best
		{
			const rayResult = this.ray && this.ray.get();
			
			if(isNaN(this.distance))
			{
				if(rayResult && isSubEntity(this.currentTarget, rayResult))
				{
					this.distance = -1;
					return this; //Explicitly targetting currentTarget
				}

				if(this.currentSlot && this.currentAnchor)
					this.distance = this.currentSlot.components.slot.sqrDistanceFrom(this.currentAnchor);
				else
					this.distance = sqrDistanceBetween(this.currentSlot || this.slot!, this.currentTarget);
			}

			const distance = 
				(rayResult && isSubEntity(detectedEntity, rayResult)) ? -1 :
				(possibleSlot && possibleAnchor) ? possibleSlot.components.slot.sqrDistanceFrom(possibleAnchor) : 
				sqrDistanceBetween(possibleSlot, possibleTarget);

			if(distance >= this.distance)
				return this;

			this.distance = distance;
		}

		this.currentTarget = possibleTarget;
		this.currentSlot = possibleSlot;
		this.currentAnchor = possibleAnchor;
		return this;
	}


	

	finish(avatarSystem : AvatarSystem, force?:boolean) : AvatarInteractionTarget 
	{
		if(this.finalized) return this;
		this.finalized = true;
		this.initialized = false;

		delete this.ray;

		if(!this.slot) return this; //Not initialized
		if(this.locked) return this;
		
		const ct = this.currentTarget, pt = this.previousTarget;
		const ca = this.currentAnchor, pa = this.previousAnchor;
		const cs = this.currentSlot, ps = this.previousSlot;
		const slotAnchorEl = this.slotItem, prevAnchorEl = this.previousSlotItem;

		const forceUpdate = force || this.updateNeeded;

		delete this.previousTarget;
		delete this.previousAnchor;
		delete this.previousSlotItem;
		delete this.updateNeeded;

		if(!forceUpdate)
		{
			if(ct === pt)
				if(ca === pa)
					if(cs === ps)
						if(slotAnchorEl === prevAnchorEl)
							return this; //No change.
		}
		


		let grabFactory : AvatarActionFactory | undefined = undefined;
		let useFactory : AvatarActionFactory | undefined = undefined;
		let fireFactory : AvatarActionFactory | undefined = undefined;
		let inspectFactory : AvatarActionFactory | undefined = undefined;

		const vr = avatarSystem.el.is("vr-mode"), ui = avatarSystem.el.is("ui-mode");
		
		const thisSlotData = this.slot.components.slot.data;

		if( slotAnchorEl ) //Something in hand
		{
			if( !vr )
			{
				inspectFactory = (avatar, input) => (this.currentSlot === cs) && isFreshInput(input) && new AnimationActionInstance(avatar, input, "Inspect");
				inspectFactory.label = "Inspect";
			}

			let grabAction = "Drop";
			let grabIcon = slotAnchorEl.components.anchor.data.icon || thisSlotData!.icon;

			const currentAnchorData = this.currentSlot && this.currentAnchor && this.currentAnchor.components.anchor.data;
			const currentSlotData = this.currentSlot && this.currentSlot.components.slot.data;
			const compatibleAnchorSlot = currentAnchorData && currentSlotData && !currentSlotData.locked;
			if(compatibleAnchorSlot && currentAnchorData.attach) //Natural interaction : attach anchor to slot
			{
				grabAction = currentSlotData.label || currentAnchorData.label || "Attach";
				grabIcon = currentSlotData.icon || currentAnchorData.icon || thisSlotData!.icon;
			}
			else // Can't attach : Store, Return or Drop
			{
				const naturalParent = thisSlotData.item?.parentNode as Entity;
				const components = (naturalParent && naturalParent.components) || {};

				if( "avatar-inventory" in components )
					grabAction = "Store";
				else if( "slot" in components )
				{
					const naturalSlotData = (components.slot as SlotComponent).data;
					grabAction = naturalSlotData.label || "Return";
					grabIcon = naturalSlotData.icon;
				}
				else if(compatibleAnchorSlot && thisSlotData.item && thisSlotData.item.components["ammo-body"])
				{
					grabAction = currentSlotData.label || currentAnchorData.label || "Put";
					grabIcon = currentSlotData.icon || currentAnchorData.icon || thisSlotData!.icon;
				}
			}

			// Release-to-inventory-item is handled by GrabAction
			grabFactory = (avatar, input) => 
				(this.currentTarget === ct) && (this.currentSlot === cs)
				&& isFreshInput(input) && new AvatarGrabAction(avatar, input, this, grabAction);
			grabFactory.label = grabAction;
			grabFactory.icon = grabIcon;

			//No lever here : only for non-handheld items
			const button = slotAnchorEl.components.button as InteractiveComponent, 
				trigger = slotAnchorEl.components.trigger as InteractiveComponent;
			
			if( button && button.data.enabled && (button.data.vr || !vr) )
			{
				useFactory = (avatar, input) => new EventEmitterAction(avatar, input, this, button );
				useFactory.label = button.data.label;
				useFactory.icon = button.data.icon;
				useFactory.cursor = button.data.cursor;
			}

			if( trigger && trigger.data.enabled && (trigger.data.vr || !vr) )
			{
				fireFactory = (avatar, input) => new EventEmitterAction(avatar, input, this, trigger);
				fireFactory.label = trigger.data.label;
				fireFactory.icon = trigger.data.icon;
				fireFactory.cursor = trigger.data.cursor;
			}
		}
		else if( ct ) //Something in reach
		{
			const anchor = ct.components.anchor as AnchorComponent;

			//If there's an anchor on a non-ui interactive element, button and trigger only work when held
			const button = (anchor && !ui) ? undefined : (ct.components.button as InteractiveComponent);
			const trigger = (anchor && !ui) ? undefined : (ct.components.trigger as InteractiveComponent);
			const lever = (anchor && !ui) ? undefined : (ct.components.lever as InteractiveComponent);

			if( anchor && anchor.data.enabled && anchor.fits(this.slot) )
			{
				if(!thisSlotData.anchor) //Can't grab something with something else held
				{
					grabFactory = (avatar, input) => (this.currentTarget === ct) && isFreshInput(input) && new AvatarGrabAction(avatar, input, this, "Take");
					grabFactory.label = anchor.data.label || thisSlotData!.label || "Take";
					grabFactory.icon = anchor.data.icon || thisSlotData!.icon;
				}
			}
			else if( lever && lever.data.enabled && (lever.data.vr || !vr) )
			{
				grabFactory = (avatar, input) => (this.currentTarget === ct) && new EventEmitterAction(avatar, input, this, lever);
				grabFactory.label = lever.data.label;
				grabFactory.icon = lever.data.icon;
				grabFactory.cursor = lever.data.cursor;
			}
			
			if( button && button.data.enabled && (button.data.vr || !vr) )
			{
				useFactory = (avatar, input) => (this.currentTarget === ct) && new EventEmitterAction(avatar, input, this, button);
				useFactory.label = button.data.label;
				useFactory.icon = button.data.icon;
				useFactory.cursor = button.data.cursor;
			}

			if( trigger && trigger.data.enabled && (trigger.data.vr || !vr) )
			{
				fireFactory = (avatar, input) => new EventEmitterAction(avatar, input, this, trigger);
				fireFactory.label = trigger.data.label;
				fireFactory.icon = trigger.data.icon;
				fireFactory.cursor = trigger.data.cursor;
			}

			//In vr, objects with a button/lever only also work with the trigger
			if( vr && !trigger )
			{
				if( useFactory )
					fireFactory = useFactory;
				else if( grabFactory && lever )
					fireFactory = grabFactory;
			}

		}

		const mainFactory = slotAnchorEl ? 
			(fireFactory || useFactory || grabFactory) :
			(grabFactory || useFactory || fireFactory);

		avatarSystem.setAction(this.mainAction, mainFactory );
		avatarSystem.setAction(this.grabAction, grabFactory);
		avatarSystem.setAction(this.useAction, useFactory);
		avatarSystem.setAction(this.fireAction, fireFactory);
		avatarSystem.setAction(this.inspectAction, inspectFactory);
		
		//Only dispatch enter/exit interactive-range when the target context really changes.
		if( /* forceUpdate || */ (pt !== ct) || (pa !== ca) || (prevAnchorEl !== slotAnchorEl) )
		{
			if(pt)
			{
				const target = (prevAnchorEl ? ps : pa) || pt;
				const source = prevAnchorEl ? (pa || prevAnchorEl) : this.slot;
				target.emit("exit-interactive-range", source, true);
				source.emit("exit-interactive-range", target, true);
				if(prevAnchorEl)
					this.slot.emit("exit-interactive-range", target, true);
			}
			if(ct)
			{
				const target = (slotAnchorEl ? cs : ca) || ct;
				const source = slotAnchorEl ? (ca || slotAnchorEl) : this.slot;
				target.emit("enter-interactive-range", source, true);
				source.emit("enter-interactive-range", target, true);
				if(slotAnchorEl)
					this.slot!.emit("enter-interactive-range", ct, true); //Hand wants to know
			}
		}

		for(let el of [pt, pa, ps, prevAnchorEl].filter(onceInArray))
			el?.removeEventListener("interactions-changed", this.updateListener);

		this.slot!.emit("actions-changed", null, false);		

		for(let el of [ct, ca, cs, slotAnchorEl].filter(onceInArray))
			el?.addEventListener("interactions-changed", this.updateListener);

		return this;
	}
}